// Wrapper around network request, can be used on client and server
import { INetworkProvider, NetworkResponse } from './networkService';

async function fetchWithErrorHandling(url: string, options?: RequestInit): Promise<any> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers || {})
    },
    ...options
  });

  // Try to parse JSON, keeping track of whether it failed
  let body: any;
  let jsonParseError: Error | null = null;
  try {
    body = await response.json();
  } catch (err) {
    jsonParseError = err instanceof Error ? err : new Error(String(err));
    body = {};
  }

  if (!response.ok) {
    // Include JSON parse error info in the error message if applicable
    const errorMessage = jsonParseError
      ? `Request failed (status ${response.status}, response was not valid JSON)`
      : body.message || `Request failed with status ${response.status}`;
    const error = new Error(errorMessage) as any;
    error.response = {
      body,
      status: response.status,
      header: Object.fromEntries(response.headers.entries())
    } as NetworkResponse;
    error.jsonParseError = jsonParseError;
    throw error;
  }

  // Log a warning in development if JSON parsing failed on a successful response
  if (jsonParseError && process.env.NODE_ENV !== 'production') {
    console.warn(`[clientNetworkProvider] Response from ${url} was not valid JSON:`, jsonParseError.message);
  }

  return body;
}

export default class ClientNetworkProvider implements INetworkProvider {
  public async get(url: string) {
    return fetchWithErrorHandling(url);
  }

  public async post(url: string, payload?: any) {
    return fetchWithErrorHandling(url, {
      method: 'POST',
      body: payload ? JSON.stringify(payload) : undefined
    });
  }

  public async put(url: string, payload?: any) {
    return fetchWithErrorHandling(url, {
      method: 'PUT',
      body: payload ? JSON.stringify(payload) : undefined
    });
  }

  public async patch(url: string, payload?: any) {
    return fetchWithErrorHandling(url, {
      method: 'PATCH',
      body: payload ? JSON.stringify(payload) : undefined
    });
  }

  public async delete(url: string, payload?: any) {
    return fetchWithErrorHandling(url, {
      method: 'DELETE',
      body: payload ? JSON.stringify(payload) : undefined
    });
  }
}
